export type ChartType =
  | "area"
  | "bar"
  | "pie"
  | "donut"
  | "line"
  | "area-line"
  | "assets_category_chart"
  | "assets_category_pie_chart"
  | "table"
  | "datatable"
  | "lead_sources_chart"
  | "daily_lead_trends"
  | "lead_sources_pie_chart"
  | "lead_sources_table"
  | "pipeline_stage_distribution"
  | "deal_sources_chart"
  | "daily_deal_trends"
  | "deal_sources_pie_chart"
  | "deal_sources_table"
  | "deal_pipeline_stage_distribution"
  | "form_key_stats_chart"
  | "lead_performers_stage_wise_table"
  | "lead_sources_stage_wise_table"
  | "deal_performers_stage_wise_table"
  | "deal_sources_stage_wise_table";

export type ValueFormat = "number" | "currency" | "percentage";
export type LegendPosition = "top" | "bottom" | "left" | "right";
export type StatCardVariant =
  | "primary"
  | "success"
  | "warning"
  | "danger"
  | "info"
  | "purple"
  | "teal"
  | "slate";

export interface MetricConfig {
  id: string;
  title: string;
  icon: string;
  description: string;
  dataPath: string; // Dot notation path to access data from backend response
  valueFormat: ValueFormat | any;
  variant: StatCardVariant;
  onlyForAdmin?: boolean;
}

export interface ChartAxisConfig {
  field: string;
  label: string;
}

export interface ChartSeriesConfig {
  dataKey: string;
  name: string;
  stroke: string;
  fill?: string;
  strokeWidth?: number;
  dot?: boolean;
  area?: boolean;
}

export interface ChartConfig {
  id: string;
  title: string;
  description: string;
  chartType: ChartType;
  icon?: string;
  dataPath: string; // Dot notation path to access data from backend response
  onlyForAdmin?: boolean;
  config: {
    // Common properties
    colors: string[];
    showLegend: boolean;
    series?: ChartSeriesConfig[];

    // Line/Area/Bar charts
    xAxis?: ChartAxisConfig;
    yAxis?: ChartAxisConfig;
    fill?: boolean;
    tension?: number;
    showGrid?: boolean;
    barWidth?: number;

    // Pie/Donut charts
    labelField?: string;
    valueField?: string;
    legendPosition?: LegendPosition;
    cutout?: string; // For donut charts
    showLabels?: boolean;
    columns?: any;
    pagination?: boolean;
    search?: boolean;
    showHeader?: boolean;
    striped?: boolean;
    highlightOnHover?: boolean;

    expandable?: boolean;
    childrenField?: string;

    type?: number;
  };
}

export interface ModuleConfig {
  metrics: Record<string, MetricConfig>;
  charts: Record<string, ChartConfig>;
}

export interface DashboardConfig {
  [moduleName: string]: ModuleConfig;
}

export const DASHBOARD_CHART_CONFIG: DashboardConfig = {
  assets: {
    metrics: {
      total: {
        id: "assets-total",
        title: "Total Assets",
        icon: "ti-box-multiple",
        description: "Total number of assets in inventory",
        dataPath: "assets.total",
        valueFormat: "number",
        variant: "primary",
        onlyForAdmin: true, // employeeAdmin
      },
      assigned: {
        id: "assets-assigned",
        title: "Assigned Assets",
        icon: "ti-checks",
        description: "Total number of assigned assets",
        dataPath: "assets.assigned",
        valueFormat: "number",
        variant: "success",
      },
      inactive: {
        id: "assets-inactive",
        title: "Inactive Assets",
        icon: "ti-devices-off",
        description: "Currently non-operational assets",
        dataPath: "assets.inactive",
        valueFormat: "number",
        variant: "danger",
        onlyForAdmin: true,
      },
      instock: {
        id: "assets-instock",
        title: "Instock Assets",
        icon: "ti-devices",
        description: "Currently in stock assets",
        dataPath: "assets.instock",
        valueFormat: "number",
        variant: "warning",
        onlyForAdmin: true,
      },
    },
    charts: {
      assets_category_chart: {
        id: "assets-category-chart",
        icon: "ti-activity",
        title: "Asset Status by Category (Area)",
        description:
          "Asset status (total, assigned, in-stock, inactive) count across different categories",
        chartType: "assets_category_chart",
        dataPath: "assets.categories",
        onlyForAdmin: true,
        config: {
          xAxis: { field: "category", label: "Categories" },
          yAxis: { field: "count", label: "Category wise count" },
          showGrid: true,
          showLegend: true,
          barWidth: 60,
          series: [
            {
              dataKey: "total",
              name: "Total Assets",
              stroke: "#8b5cf6",
              fill: "#8b5cf6",
            },
            {
              dataKey: "assigned",
              name: "Assigned",
              stroke: "#3b82f6",
              fill: "#3b82f6",
            },
            {
              dataKey: "instock",
              name: "In Stock",
              stroke: "#10b981",
              fill: "#10b981",
            },
            {
              dataKey: "inactive",
              name: "Inactive",
              stroke: "#ef4444",
              fill: "#ef4444",
            },
          ],
          colors: ["#8b5cf6", "#3b82f6", "#10b981", "#ef4444"],
        },
      },
      asset_category_pie_chart: {
        id: "assets-activity-pie-chart",
        icon: "ti-activity",
        title: "Asset Status by Category (Pie)",
        description:
          "Asset status (total, assigned, in-stock, inactive) count across different categories",
        chartType: "assets_category_pie_chart",
        dataPath: "assets.categories",
        onlyForAdmin: true,
        config: {
          xAxis: { field: "category", label: "Categories" },
          yAxis: { field: "count", label: "Category wise count" },
          showGrid: true,
          showLegend: true,
          barWidth: 60,
          series: [
            {
              dataKey: "total",
              name: "Total Assets",
              stroke: "#8b5cf6",
              fill: "#8b5cf6",
            },
            {
              dataKey: "assigned",
              name: "Assigned",
              stroke: "#3b82f6",
              fill: "#3b82f6",
            },
            {
              dataKey: "instock",
              name: "In Stock",
              stroke: "#10b981",
              fill: "#10b981",
            },
            {
              dataKey: "inactive",
              name: "Inactive",
              stroke: "#ef4444",
              fill: "#ef4444",
            },
          ],
          colors: ["#8b5cf6", "#3b82f6", "#10b981", "#ef4444"],
        },
      },
    },
  },

  calls: {
    metrics: {
      total: {
        id: "calls-total",
        title: "Total Calls",
        icon: "ti-phone-call",
        description: "Total number of calls made during the selected period.",
        dataPath: "calls.total",
        valueFormat: "number",
        variant: "primary",
      },
      connected: {
        id: "calls-connected",
        title: "Connected Calls",
        icon: "ti-phone-check",
        description: "Number of calls successfully connected to customers.",
        dataPath: "calls.connected",
        valueFormat: "number",
        variant: "success",
      },
      disconnected: {
        id: "calls-disconnected",
        title: "Disconnected Calls",
        icon: "ti-phone-off",
        description:
          "Number of calls that were not connected or went unanswered.",
        dataPath: "calls.disconnected",
        valueFormat: "number",
        variant: "danger",
      },
      total_call_time: {
        id: "calls-total-duration",
        title: "Total Call Duration",
        icon: "ti-clock-2",
        description: "Cumulative duration of all connected calls in minutes.",
        dataPath: "calls.total_call_time",
        valueFormat: "duration",
        variant: "info",
      },
      avg_call_time: {
        id: "calls-average-duration",
        title: "Average Call Duration",
        icon: "ti-time-duration-10",
        description: "Average duration per connected call.",
        dataPath: "calls.avg_call_time",
        valueFormat: "avg-duration",
        variant: "warning",
      },
      missed_calls: {
        id: "calls-missed",
        title: "Missed Calls",
        icon: "ti-phone-incoming",
        description: "Total number of missed calls.",
        dataPath: "calls.missed_calls",
        valueFormat: "number",
        variant: "warning",
      },
    },
    charts: {
      user_call_summary: {
        id: "calls-user-summary-table",
        title: "User Call Summary",
        icon: "ti-users",
        description:
          "List of users with their total, connected, and missed call counts.",
        chartType: "table",
        dataPath: "calls.user_call_summary",
        config: {
          colors: ["#10b981"],
          fill: true,
          tension: 0.4,
          showGrid: true,
          showLegend: false,
          columns: "calls_columns",
          pagination: true,
          search: true,
          showHeader: true,
          striped: true,
          highlightOnHover: true,
        },
      },
    },
  },

  attendance: {
    metrics: {
      present_percentage: {
        id: "attendance-present-percentage",
        title: "Present %",
        icon: "ti-user-check",
        description: "Percentage of employees marked present.",
        dataPath: "attendance.present_percentage",
        valueFormat: "percentage",
        variant: "success",
      },
      absent_percentage: {
        id: "attendance-absent-percentage",
        title: "Absent %",
        icon: "ti-user-x",
        description: "Percentage of employees marked absent.",
        dataPath: "attendance.absent_percentage",
        valueFormat: "percentage",
        variant: "danger",
      },
      late_percentage: {
        id: "attendance-late-percentage",
        title: "Late %",
        icon: "ti-alarm-minus",
        description: "Percentage of employees who checked in late.",
        dataPath: "attendance.late_percentage",
        valueFormat: "percentage",
        variant: "warning",
      },
      total_working_hours: {
        id: "attendance-total-working-hours",
        title: "Total Working Hours",
        icon: "ti-clock-hour-4",
        description: "Cumulative working hours of all present employees.",
        dataPath: "attendance.total_working_hours",
        valueFormat: "duration",
        variant: "info",
      },
      avg_working_hours: {
        id: "attendance-avg-working-hours",
        title: "Avg Working Hours",
        icon: "ti-clock",
        description: "Average working hours per employee.",
        dataPath: "attendance.avg_working_hours",
        valueFormat: "duration",
        variant: "primary",
      },
      avg_punch_in_time: {
        id: "attendance-avg-punch-in",
        title: "Avg Check-in Time",
        icon: "ti-login",
        description: "Average check-in time for employees.",
        dataPath: "attendance.avg_checkin_time",
        valueFormat: "time",
        variant: "teal",
      },
      avg_punch_out_time: {
        id: "attendance-avg-punch-out",
        title: "Avg Check-out Time",
        icon: "ti-logout",
        description: "Average check-out time for employees.",
        dataPath: "attendance.avg_checkout_time",
        valueFormat: "time",
        variant: "teal",
      },
      avg_late_time: {
        id: "attendance-avg-late-time",
        title: "Avg Late Time",
        icon: "ti-clock-question",
        description: "Average late duration for employees who checked in late.",
        dataPath: "attendance.avg_late_time",
        valueFormat: "avg-duration",
        variant: "warning",
      },
      total_late_seconds: {
        id: "attendance-total-late-minutes",
        title: "Total Late Time",
        icon: "ti-clock-hour-7",
        description: "Total accumulated late minutes.",
        dataPath: "attendance.total_late_seconds",
        valueFormat: "avg-duration",
        variant: "warning",
      },
    },
    charts: {
      user_attendance_summary: {
        id: "attendance-user-attendance-summary-table",
        title: "User Attendance Summary",
        icon: "ti-users",
        description: "List of users with their attendance summary",
        chartType: "table",
        dataPath: "attendance.user_attendance_summary",
        config: {
          colors: ["#10b981"],
          fill: true,
          tension: 0.4,
          showGrid: true,
          showLegend: false,
          columns: "attendance_columns",
          pagination: true,
          search: true,
          showHeader: true,
          striped: true,
          highlightOnHover: true,
        },
      },
    },
  },

  leads: {
    metrics: {
      total_new: {
        id: "leads-total-new",
        title: "New Leads",
        icon: "ti-timeline-event-plus",
        description: "Total new leads generated in the selected period",
        dataPath: "leads.total_new",
        valueFormat: "number",
        variant: "primary",
      },
      converted: {
        id: "leads-converted",
        title: "Converted Leads",
        icon: "ti-receipt-rupee",
        description: "Leads successfully converted in the selected period",
        dataPath: "leads.converted",
        valueFormat: "number",
        variant: "success",
      },
      lost: {
        id: "leads-lost",
        title: "Lost Leads",
        icon: "ti-briefcase-off",
        description: "Leads marked as lost in the selected period",
        dataPath: "leads.lost",
        valueFormat: "number",
        variant: "danger",
      },
      in_process: {
        id: "leads-in-process",
        title: "In Process",
        icon: "ti-progress-bolt",
        description: "Leads currently being worked on",
        dataPath: "leads.in_process",
        valueFormat: "number",
        variant: "warning",
      },
      conversion_rate: {
        id: "leads-conversion-rate",
        title: "Conversion Rate",
        icon: "ti-home-stats",
        description: "Percentage of leads converted during the period",
        dataPath: "leads.conversion_rate",
        valueFormat: "percentage",
        variant: "success",
      },
      losing_rate: {
        id: "leads-losing-rate",
        title: "Losing Rate",
        icon: "ti-chart-histogram",
        description: "Percentage of leads lost during the period",
        dataPath: "leads.losing_rate",
        valueFormat: "percentage",
        variant: "danger",
      },
    },

    charts: {
      lead_sources_chart: {
        id: "leads-source-nested-chart",
        title: "Lead Source Distribution (Area)",
        icon: "ti-chart-treemap",
        description:
          "Hierarchical view of lead sources with parent categories and child subcategories",
        chartType: "lead_sources_chart",
        dataPath: "leads.sources",
        config: {
          valueField: "total_leads",
          showLegend: true,
          showGrid: true,
          series: [
            {
              dataKey: "total_leads",
              name: "Total Leads",
              stroke: "#6366f1",
              fill: "#6366f1",
            },
            {
              dataKey: "converted",
              name: "Converted",
              stroke: "#10b981",
              fill: "#10b981",
            },
            {
              dataKey: "lost",
              name: "Lost",
              stroke: "#ef4444",
              fill: "#ef4444",
            },
            {
              dataKey: "in_process",
              name: "In Process",
              stroke: "#f59e0b",
              fill: "#f59e0b",
            },
          ],
          colors: ["#6366f1", "#10b981", "#ef4444", "#f59e0b"],
        },
      },
      performer_table: {
        id: "leads-performer-table",
        title: "Lead Performance Rankings (Table)",
        icon: "ti-users-group",
        description:
          "Ranked list of employees by conversion rate and lead performance",
        chartType: "table",
        dataPath: "leads.performers",
        onlyForAdmin: true,
        config: {
          columns: "lead_performer_columns",
          showLegend: true,
          pagination: true,
          search: true,
          showHeader: true,
          striped: true,
          highlightOnHover: true,
          colors: ["#10b981"],
        },
      },
      daily_lead_trends: {
        id: "leads-daily-trends",
        title: "Daily Lead Performance (Area)",
        icon: "ti-calendar-stats",
        description:
          "Day-wise tracking of new, converted, lost, and in-process leads",
        chartType: "daily_lead_trends",
        dataPath: "leads.daily_trends",
        config: {
          xAxis: { field: "date", label: "Date" },
          yAxis: { field: "count", label: "Number of Leads" },
          showLegend: true,
          showGrid: true,
          series: [
            {
              dataKey: "new",
              name: "New Leads",
              stroke: "#3b82f6",
              fill: "#3b82f620",
            },
            {
              dataKey: "converted",
              name: "Converted",
              stroke: "#10b981",
              fill: "#10b98120",
            },
            {
              dataKey: "lost",
              name: "Lost",
              stroke: "#ef4444",
              fill: "#ef444420",
            },
            {
              dataKey: "in_process",
              name: "In Process",
              stroke: "#f59e0b",
              fill: "#f59e0b20",
            },
          ],
          colors: ["#3b82f6", "#10b981", "#ef4444", "#f59e0b"],
        },
      },
      lead_sources_pie_chart: {
        id: "leads-source-pie",
        title: "Lead Sources Overview (Pie)",
        icon: "ti-chart-pie",
        description:
          "Distribution of total leads across parent source categories",
        chartType: "lead_sources_pie_chart",
        dataPath: "leads.sources",
        config: {
          labelField: "source",
          valueField: "total_leads",
          colors: ["#f59e0b", "#8b5cf6", "#06b6d4", "#22c55e", "#ef4444"],
          showLabels: true,
          showLegend: true,
          legendPosition: "right",
        },
      },
      lead_sources_table: {
        id: "leads-source-table",
        title: "Lead Sources Performance (Table)",
        icon: "ti-table",
        description:
          "Detailed breakdown of lead sources with nested children and conversion metrics",
        chartType: "lead_sources_table",
        dataPath: "leads.sources",
        config: {
          columns: "lead_sources_columns",
          pagination: true,
          search: true,
          showHeader: true,
          striped: true,
          highlightOnHover: true,
          expandable: true,
          childrenField: "children",
          colors: ["#10b981"],
          showLegend: true,
        },
      },
      pipeline_stage_distribution_chart: {
        id: "pipeline-stage-funnel",
        title: "Pipeline Stage Distribution (Funnel)",
        icon: "ti-chart-funnel",
        description:
          "Visual funnel representation of lead distribution across pipeline stages with conversion flow",
        chartType: "pipeline_stage_distribution",
        dataPath: "leads.pipeline_wise_distribution",
        config: {
          xAxis: { field: "date", label: "Date" },
          yAxis: { field: "deals", label: "Deals Closed" },
          colors: ["#8b5cf6"],
          fill: true,
          tension: 0.4,
          showGrid: true,
          showLegend: true,
          pagination: true,
          search: true,
          showHeader: true,
        },
      },
      leads_form_key_stats_chart: {
        id: "leads-form_key_stats_chart",
        title: "Leads by Dynamic Form Keys (Bar)",
        icon: "ti-presentation-analytics",
        description:
          "Distribution of leads by dynamic form keys (city, state, country, etc.)",
        chartType: "form_key_stats_chart",
        dataPath: "",
        config: {
          colors: ["#10b981", "#f59e0b", "#ef4444"],
          showGrid: true,
          showLegend: true,
          type: 1, // 1 for lead, 2 for deal
        },
      },
      lead_sources_stage_wise_table: {
        id: "leads-sources-stage-wise-table",
        title: "Lead Sources by Pipeline Stage (Table)",
        icon: "ti-table-options",
        description:
          "Detailed stage-wise breakdown of lead sources showing distribution across pipeline stages",
        chartType: "lead_sources_stage_wise_table",
        dataPath: "leads.sources_stage_wise",
        config: {
          pagination: true,
          search: true,
          showHeader: true,
          striped: true,
          highlightOnHover: true,
          expandable: true,
          childrenField: "children",
          colors: ["#10b981"],
          showLegend: true,
        },
      },

      lead_performers_stage_wise_table: {
        id: "leads-performers-stage-wise-table",
        title: "Lead Performers by Pipeline Stage (Table)",
        icon: "ti-users-group",
        description:
          "Employee performance breakdown showing lead distribution across pipeline stages",
        chartType: "lead_performers_stage_wise_table",
        dataPath: "leads.performers_stage_wise",
        onlyForAdmin: true,
        config: {
          pagination: true,
          search: true,
          showHeader: true,
          striped: true,
          highlightOnHover: true,
          colors: ["#10b981"],
          showLegend: true,
        },
      },
    },
  },

  deals: {
    metrics: {
      total_new: {
        id: "deals-total-new",
        title: "New Deals",
        icon: "ti-timeline-event-plus",
        description: "Total new deals generated in the selected period",
        dataPath: "deals.total_new",
        valueFormat: "number",
        variant: "primary",
      },
      converted: {
        id: "deals-converted",
        title: "Converted Deals",
        icon: "ti-receipt-rupee",
        description: "Deals successfully converted in the selected period",
        dataPath: "deals.converted",
        valueFormat: "number",
        variant: "success",
      },
      lost: {
        id: "deals-lost",
        title: "Lost Leads",
        icon: "ti-briefcase-off",
        description: "Deals marked as lost in the selected period",
        dataPath: "deals.lost",
        valueFormat: "number",
        variant: "danger",
      },
      in_process: {
        id: "deals-in-process",
        title: "In Process",
        icon: "ti-progress-bolt",
        description: "Deals currently being worked on",
        dataPath: "deals.in_process",
        valueFormat: "number",
        variant: "warning",
      },
      conversion_rate: {
        id: "deals-conversion-rate",
        title: "Conversion Rate",
        icon: "ti-home-stats",
        description: "Percentage of deals converted during the period",
        dataPath: "deals.conversion_rate",
        valueFormat: "percentage",
        variant: "success",
      },
      losing_rate: {
        id: "deals-losing-rate",
        title: "Losing Rate",
        icon: "ti-chart-histogram",
        description: "Percentage of deals lost during the period",
        dataPath: "deals.losing_rate",
        valueFormat: "percentage",
        variant: "danger",
      },
    },

    charts: {
      deal_sources_chart: {
        id: "deals-source-nested-chart",
        title: "Deal Source Distribution (Area)",
        icon: "ti-chart-treemap",
        description:
          "Hierarchical view of deal sources with parent categories and child subcategories",
        chartType: "deal_sources_chart",
        dataPath: "deals.sources",
        config: {
          valueField: "total_leads",
          showLegend: true,
          showGrid: true,
          series: [
            {
              dataKey: "total_leads",
              name: "Total Leads",
              stroke: "#6366f1",
              fill: "#6366f1",
            },
            {
              dataKey: "converted",
              name: "Converted",
              stroke: "#10b981",
              fill: "#10b981",
            },
            {
              dataKey: "lost",
              name: "Lost",
              stroke: "#ef4444",
              fill: "#ef4444",
            },
            {
              dataKey: "in_process",
              name: "In Process",
              stroke: "#f59e0b",
              fill: "#f59e0b",
            },
          ],
          colors: ["#6366f1", "#10b981", "#ef4444", "#f59e0b"],
        },
      },
      deals_performer_table: {
        id: "deals-performer-table",
        title: "Deal Performance Rankings (Table)",
        icon: "ti-users-group",
        description:
          "Ranked list of employees by conversion rate and deal performance",
        chartType: "table",
        dataPath: "deals.performers",
        onlyForAdmin: true,
        config: {
          columns: "lead_performer_columns",
          showLegend: true,
          pagination: true,
          search: true,
          showHeader: true,
          striped: true,
          highlightOnHover: true,
          colors: ["#10b981"],
        },
      },
      daily_deal_trends: {
        id: "deals-daily-trends",
        title: "Daily Deals Performance (Area)",
        icon: "ti-calendar-stats",
        description:
          "Day-wise tracking of new, converted, lost, and in-process deals",
        chartType: "daily_deal_trends",
        dataPath: "deals.daily_trends",
        config: {
          xAxis: { field: "date", label: "Date" },
          yAxis: { field: "count", label: "Number of Leads" },
          showLegend: true,
          showGrid: true,
          series: [
            {
              dataKey: "new",
              name: "New Leads",
              stroke: "#3b82f6",
              fill: "#3b82f620",
            },
            {
              dataKey: "converted",
              name: "Converted",
              stroke: "#10b981",
              fill: "#10b98120",
            },
            {
              dataKey: "lost",
              name: "Lost",
              stroke: "#ef4444",
              fill: "#ef444420",
            },
            {
              dataKey: "in_process",
              name: "In Process",
              stroke: "#f59e0b",
              fill: "#f59e0b20",
            },
          ],
          colors: ["#3b82f6", "#10b981", "#ef4444", "#f59e0b"],
        },
      },
      deal_sources_pie_chart: {
        id: "deal-source-pie",
        title: "Deal Sources Overview (Pie)",
        icon: "ti-chart-pie",
        description:
          "Distribution of total deals across parent source categories",
        chartType: "deal_sources_pie_chart",
        dataPath: "deals.sources",
        config: {
          labelField: "source",
          valueField: "total_leads",
          colors: ["#f59e0b", "#8b5cf6", "#06b6d4", "#22c55e", "#ef4444"],
          showLabels: true,
          showLegend: true,
          legendPosition: "right",
        },
      },
      deal_sources_table: {
        id: "deals-source-table",
        title: "Deal Sources Performance (Table)",
        icon: "ti-table",
        description:
          "Detailed breakdown of deal sources with nested children and conversion metrics",
        chartType: "deal_sources_table",
        dataPath: "deals.sources",
        config: {
          columns: "lead_sources_columns",
          pagination: true,
          search: true,
          showHeader: true,
          striped: true,
          highlightOnHover: true,
          expandable: true,
          childrenField: "children",
          colors: ["#10b981"],
          showLegend: true,
        },
      },
      deal_pipeline_stage_distribution_chart: {
        id: "deal-pipeline-stage-funnel",
        title: "Deal Pipeline Stage Distribution (Funnel)",
        icon: "ti-chart-funnel",
        description:
          "Visual funnel representation of deal distribution across pipeline stages with conversion flow",
        chartType: "deal_pipeline_stage_distribution",
        dataPath: "deals.pipeline_wise_distribution",
        config: {
          xAxis: { field: "date", label: "Date" },
          yAxis: { field: "deals", label: "Deals Closed" },
          colors: ["#8b5cf6"],
          fill: true,
          tension: 0.4,
          showGrid: true,
          showLegend: true,
          pagination: true,
          search: true,
          showHeader: true,
        },
      },
      deals_form_key_stats_chart: {
        id: "deals-form_key_stats_chart",
        title: "Deals by Dynamic Form Keys (Bar)",
        icon: "ti-presentation-analytics",
        description:
          "Distribution of deals by dynamic form keys (city, state, country, etc.)",
        chartType: "form_key_stats_chart",
        dataPath: "",
        config: {
          colors: ["#10b981", "#f59e0b", "#ef4444"],
          showGrid: true,
          showLegend: true,
          type: 2, // 1 for lead, 2 for deal
        },
      },
      deal_sources_stage_wise_table: {
        id: "deals-sources-stage-wise-table",
        title: "Deal Sources by Pipeline Stage (Table)",
        icon: "ti-table-options",
        description:
          "Detailed stage-wise breakdown of deal sources showing distribution across pipeline stages",
        chartType: "deal_sources_stage_wise_table",
        dataPath: "deals.sources_stage_wise",
        config: {
          pagination: true,
          search: true,
          showHeader: true,
          striped: true,
          highlightOnHover: true,
          expandable: true,
          childrenField: "children",
          colors: ["#10b981"],
          showLegend: true,
        },
      },

      deal_performers_stage_wise_table: {
        id: "deals-performers-stage-wise-table",
        title: "Deal Performers by Pipeline Stage (Table)",
        icon: "ti-users-group",
        description:
          "Employee performance breakdown showing deal distribution across pipeline stages",
        chartType: "deal_performers_stage_wise_table",
        dataPath: "deals.performers_stage_wise",
        onlyForAdmin: true,
        config: {
          pagination: true,
          search: true,
          showHeader: true,
          striped: true,
          highlightOnHover: true,
          colors: ["#10b981"],
          showLegend: true,
        },
      },
    },
  },
};

export const CHART_COMPONENT_MAP = {
  area: "AreaChartComponent",
  bar: "BarChartComponent",
  pie: "PieChartComponent",
  donut: "DonutChartComponent",
  line: "LineChartComponent",
  "area-line": "AreaLineChart",
} as const;
